import { Fragment, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';
import {
  DayOfWeekEnum,
  useInsertUserCustomCourtMutation,
  useUpdateCoachAvailabilityMutation,
} from 'types/generated/client';
import {
  DEFAULT_COACH_AVAILABILITY,
  FinalTimeSlot,
  LocationType,
  MAX_END_TIME_MINUTES,
  MINIMUM_TIME_FROM_START_MINUTES,
  VenueAddress,
  addressExists,
  dayOfWeekMap,
  hourOptions,
  mergeCoachAvailability,
  unMergedDTimeSlots,
  zodAlwaysRefine,
} from 'utils/shared/coachBuilder';
import { useCoachVenues } from 'hooks/useCoachVenues';
import useBrowserStorage from 'hooks/useBrowserStorage';
import { useModal } from 'hooks/useModal';
import { useRequestStatus } from 'hooks/useRequestStatus';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import PlusPlain from 'svg/PlusPlain';
import Button, { ButtonText } from 'components/Button';
import ScheduleItem from 'components/coaches-builder/ScheduleItem';
import AddVenue from 'components/forms/AddVenue';
import Switcher from 'components/forms/Switcher';
import CheckboxType from 'components/forms/checkboxes/CheckBoxType';
import classNames from 'styles/utils/classNames';

export interface EditScheduleRef {
  validateAndGetValues: () => Promise<[boolean, { reducedData: FinalTimeSlot[] }]>;
}

interface EditScheduleProps {
  openDrawer?: boolean;
  setOpenDrawer?: (open: boolean) => void;
  schedule: any;
  handleSuccessfulEdit?: () => void;
  inDrawer?: boolean;
  wrapperClassName?: string;
  invert?: boolean;
}

type CoachAvailabilityPath = `coachAvailability.${number}.timeSlots.${number}.venueId`;

const EditSchedule = forwardRef(
  (
    {
      openDrawer,
      setOpenDrawer,
      schedule,
      handleSuccessfulEdit,
      inDrawer = true,
      wrapperClassName,
      invert,
    }: EditScheduleProps,
    parentConrolRef,
  ) => {
    const {
      openModal: openVenueModal,
      isOpen: isVenueModalOpen,
      closeModal: closeVenueModal,
    } = useModal();

    const [initialCoachAvailability, setInitialCoachAvailability] =
      useState<z.infer<typeof dayAvailabilitySchema>[]>();

    const [storedAddresses, setStoredAddresses, clearStoredAddresses] = useBrowserStorage<
      VenueAddress[]
    >('addresses', []);

    const venueFieldToSetRef = useRef<CoachAvailabilityPath | null>(null);

    const viewer = useViewer();
    const { userId } = viewer;

    // Use the new useCoachVenues hook
    const {
      venues: coachVenues,
      loading: isLoadingCoachVenues,
      addVenue: addCoachVenue,
      refetch: refetchCoachVenues,
    } = useCoachVenues({
      userId,
      fetchOnMount: true,
    });

    const [updateCoachAvailability, { loading: isUpdateCoachAvailability }] =
      useUpdateCoachAvailabilityMutation();

    const [insertUserCustomCourtMutation] = useInsertUserCustomCourtMutation();

    const dayAvailabilitySchema = z.object({
      checked: z.boolean(),
      dayOfWeek: z.enum([
        DayOfWeekEnum.Sunday,
        DayOfWeekEnum.Monday,
        DayOfWeekEnum.Tuesday,
        DayOfWeekEnum.Wednesday,
        DayOfWeekEnum.Thursday,
        DayOfWeekEnum.Friday,
        DayOfWeekEnum.Saturday,
      ]),
      timeSlots: z.array(
        z.object({
          startTimeMinutes: z.number().min(0).max(1440),
          endTimeMinutes: z.number().min(0).max(1440),
          venueId: z.string().optional(),
        }),
      ),
    });

    const refinedDayAvailabilitySchema = zodAlwaysRefine(dayAvailabilitySchema).superRefine(
      (data, ctx) => {
        // Check that start time is less than end time for each time slot
        for (const [i, timeSlot] of data.timeSlots.entries()) {
          if (timeSlot.startTimeMinutes >= timeSlot.endTimeMinutes) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Start time must be less than end time',
              path: [`timeSlots.${i}.startTimeMinutes`],
            });
          }
        }

        // Check for overlapping time slots
        for (let i = 0; i < data.timeSlots.length; i++) {
          for (let j = i + 1; j < data.timeSlots.length; j++) {
            const slot1 = data.timeSlots[i];
            const slot2 = data.timeSlots[j];
            if (
              slot1.startTimeMinutes < slot2.endTimeMinutes &&
              slot1.endTimeMinutes > slot2.startTimeMinutes
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Time slots must not overlap',
                path: [`timeSlots.${i}.startTimeMinutes`],
              });
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Time slots must not overlap',
                path: [`timeSlots.${j}.startTimeMinutes`],
              });
            }
          }
        }

        if (data.checked) {
          for (const [i, timeSlot] of data.timeSlots.entries()) {
            if (!timeSlot.venueId || timeSlot.venueId.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Venue is required for checked days',
                path: [`timeSlots.${i}.venueId`],
              });
            }
          }
        }
      },
    );
    const {
      isLoading: isLoading,
      setLoading: setLoading,
      setSuccess: setSuccess,
      setError: setError,
    } = useRequestStatus();

    const coachSettingsSchema = z
      .object({
        coachAvailability: z.array(refinedDayAvailabilitySchema), //schedule
        sameScheduleForMultipleDays: z.boolean().optional(),
      })
      .superRefine((data, ctx) => {
        // Check that venueId is present in each time slot if sameScheduleForMultipleDays is true
        if (data.sameScheduleForMultipleDays) {
          const NoSlotChecked = data.coachAvailability.every(
            (availability) => !availability.checked,
          );
          if (NoSlotChecked) return;
          for (const [i, timeSlot] of data.coachAvailability[0].timeSlots.entries()) {
            if (!timeSlot.venueId || timeSlot.venueId.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Venue is required for checked days',
                path: [`coachAvailability.${0}.timeSlots.${i}.venueId`],
              });
            }
          }
        }
      });

    const {
      control,
      formState: { errors },
      getValues,
      watch,
      setValue,
      handleSubmit,
      trigger,
      clearErrors: clearFormErrors,
      setError: setFormError,
    } = useForm<z.infer<typeof coachSettingsSchema>>({
      resolver: zodResolver(coachSettingsSchema),
      reValidateMode: 'onChange',
      mode: 'onChange',
      defaultValues: {
        coachAvailability: [],
        sameScheduleForMultipleDays: false,
      },
    });

    useImperativeHandle(parentConrolRef, () => ({
      validateAndGetValues: async () => {
        const isValidated = await trigger('coachAvailability');
        const data = getValues();
        const dateTimeSlots = unMergedDTimeSlots(data?.coachAvailability, storedAddresses);
        return [isValidated, dateTimeSlots];
      },
    }));

    const sameScheduleForMultipleDays = watch('sameScheduleForMultipleDays');

    const dayOfWeekToShortName = (dayOfWeek: DayOfWeekEnum) => {
      return dayOfWeekMap[dayOfWeek];
    };

    const addTimeSlot = (dayIndex: number) => {
      const timeSlotsUpdate = { ...fields[dayIndex] };

      const isChecked = watch(`coachAvailability.${dayIndex}.checked`);
      timeSlotsUpdate.checked = isChecked;
      const timeSlots = timeSlotsUpdate?.timeSlots;

      timeSlots.push({
        startTimeMinutes: timeSlots[timeSlots?.length - 1].endTimeMinutes,
        endTimeMinutes:
          timeSlots[timeSlots?.length - 1].endTimeMinutes + MINIMUM_TIME_FROM_START_MINUTES,
        venueId: '',
      });

      update(dayIndex, {
        ...timeSlotsUpdate,
        timeSlots,
      });
    };

    const removeTimeSlot = (dayIndex: number, slotIndex: number) => {
      const timeSlotsUpdate = fields[dayIndex];
      timeSlotsUpdate?.timeSlots?.splice(slotIndex, 1);

      update(dayIndex, {
        ...timeSlotsUpdate,
        timeSlots: timeSlotsUpdate?.timeSlots,
      });
    };

    const handleChangeMultipleDays = (
      startTimeMinutes: number,
      endTimeMinutes: number,
      venueId: string,
      indexSlot: number,
    ) => {
      const latestDays = getValues('coachAvailability');
      latestDays.forEach((latestDay, index) => {
        let timeSlotsUpdate = latestDay.timeSlots;
        if (latestDay.checked) {
          timeSlotsUpdate[indexSlot].startTimeMinutes = startTimeMinutes;
          timeSlotsUpdate[indexSlot].endTimeMinutes = endTimeMinutes;
          timeSlotsUpdate[indexSlot].venueId = venueId;
          update(index, {
            ...latestDay,
            timeSlots: timeSlotsUpdate,
          });
        }
      });
    };

    const getScheduleItemForMultipleDays = (
      days: z.infer<typeof dayAvailabilitySchema>[],
      addTimeSlot: (index: number) => void,
      removeTimeSlot: (index: number, indexSlot: number) => void,
    ) => {
      if (days.length === 0) return null;
      const day = days[0];
      return (
        <div className="flex flex-col gap-4">
          {day.timeSlots?.map((availabilityOnDay, indexSlot) => {
            const isEndTimeExceedingLimit =
              availabilityOnDay.endTimeMinutes ===
                hourOptions[hourOptions.length - 1].minutesAfterMidnight ||
              availabilityOnDay.endTimeMinutes > MAX_END_TIME_MINUTES;

            const key = JSON.stringify(availabilityOnDay); // unique key is necessary to let form pick new changes
            const venueFieldName = `coachAvailability.${0}.timeSlots.${indexSlot}.venueId`;
            const startTimeFieldName = `coachAvailability.${0}.timeSlots.${indexSlot}.startTimeMinutes`;
            const endTimeFieldName = `coachAvailability.${0}.timeSlots.${indexSlot}.endTimeMinutes`;
            return (
              <div key={key}>
                <div className="flex gap-2">
                  <ScheduleItem
                    control={control}
                    venueFieldName={venueFieldName}
                    startTimeFieldName={startTimeFieldName}
                    endTimeFieldName={endTimeFieldName}
                    venues={storedAddresses}
                    invert={invert}
                    onAddVenue={() => {
                      setOpenDrawer?.(false);
                      handleTriggerAddVenue(venueFieldName as CoachAvailabilityPath);
                    }}
                    handleSameScheduleForMultipleDays
                    onChangeMultipleDays={(...props) =>
                      handleChangeMultipleDays(...props, indexSlot)
                    }
                  />

                  <div className="ml-2 flex w-5 shrink-0 items-center space-x-2">
                    <CloseIcon
                      onClick={() => {
                        fields.forEach((_, index) => {
                          removeTimeSlot(index, indexSlot);
                        });
                      }}
                      className={classNames(
                        indexSlot > 0 && '!block',
                        'hidden w-5 flex-shrink-0',
                        invert && 'text-color-text-lightmode-invert',
                      )}
                    />
                  </div>
                </div>

                {indexSlot === day?.timeSlots?.length - 1 && !isEndTimeExceedingLimit && (
                  <span
                    className="typography-product-button-label-large mt-4 inline-flex cursor-pointer gap-1 text-color-brand-primary"
                    onClick={() => {
                      fields.forEach((_, index) => {
                        addTimeSlot(index);
                      });
                    }}
                  >
                    <PlusPlain className="h-5 w-5" />
                    Add Time
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    useEffect(() => {
      const validateTimeSlots = async (availability: any, index: number) => {
        const refinementValidationResult = await refinedDayAvailabilitySchema.safeParseAsync(
          availability,
        );
        if (!refinementValidationResult.success) {
          clearFormErrors(`coachAvailability.${index}.timeSlots`);
          refinementValidationResult.error.errors.forEach((err) => {
            if (err.code !== 'custom' || `${err.path?.[0]}`?.includes('venueId')) return;
            const path = `coachAvailability.${index}.${err.path.join(
              '.',
            )}` as unknown as keyof z.infer<typeof coachSettingsSchema>;
            setFormError(path, { type: 'manual', message: err.message });
          });
        } else {
          clearFormErrors(`coachAvailability.${index}.timeSlots`);
        }
      };

      const subscription = watch((value, { name }) => {
        const match = name?.match(
          /^coachAvailability\.(\d+)\.timeSlots\.\d+\.(startTimeMinutes|endTimeMinutes)$/,
        );
        if (match) {
          const index = parseInt(match[1], 10);
          const availability = value.coachAvailability?.[index];
          if (availability && availability.checked) {
            validateTimeSlots(availability, index);
          }
        }
      });
      return () => subscription.unsubscribe();
    }, [watch, setFormError, clearFormErrors, getValues]);

    const handleAddVenue = async (venue: VenueAddress) => {
      let newVenue = { ...venue };
      let venueToUse = newVenue;

      if (!venue.id) {
        if (addressExists(storedAddresses, venue.address)) {
          const existingVenue = storedAddresses.find(
            (storedAddress) => storedAddress.address === venue.address,
          );
          if (existingVenue) venueToUse = existingVenue;
        } else {
          const responseCourt = await insertUserCustomCourtMutation({
            variables: {
              customCourtData: {
                userId: viewer.userId,
                title: '',
                fullAddress: venue?.address ? venue?.address.trim() : '',
                latitude: venue.latitude,
                longitude: venue.longitude,
              },
            },
          });

          newVenue.id = responseCourt.data?.insertUserCustomCourtsOne?.id;
          venueToUse = newVenue;

          setStoredAddresses([...storedAddresses, newVenue]);
          addCoachVenue(newVenue);
        }
      } else {
        const existingVenue = storedAddresses.find(
          (storedAddress) => storedAddress.id === venue.id,
        );

        if (existingVenue) {
          venueToUse = existingVenue;
        } else {
          setStoredAddresses([...storedAddresses, newVenue]);
          addCoachVenue(newVenue);
          venueToUse = newVenue;
        }
      }

      if (venueFieldToSetRef.current) {
        if (sameScheduleForMultipleDays) {
          const [, availabilityIndex, , indexSlot] = venueFieldToSetRef.current.split('.');
          const timeSlot = getValues('coachAvailability')[+availabilityIndex];
          const startTime = timeSlot.timeSlots[+indexSlot].startTimeMinutes;
          const endTime = timeSlot.timeSlots[+indexSlot].endTimeMinutes;
          handleChangeMultipleDays(startTime, endTime, venueToUse.id, +indexSlot);
        } else {
          setValue(venueFieldToSetRef.current, venueToUse.id);
        }
        venueFieldToSetRef.current = null;
      }
    };

    const { fields, append, update } = useFieldArray({
      control,
      name: 'coachAvailability',
    });

    const handleTriggerAddVenue = (venueFieldToSet: CoachAvailabilityPath) => {
      venueFieldToSetRef.current = venueFieldToSet;
      openVenueModal();
    };

    const handleCloseVenueModal = () => {
      closeVenueModal();
      setOpenDrawer?.(true);
    };

    useEffect(() => {
      const isSameScheduleForMultipleDays = sameScheduleForMultipleDays;
      if (isSameScheduleForMultipleDays) {
        setValue('coachAvailability', DEFAULT_COACH_AVAILABILITY);
      } else {
        setValue('coachAvailability', initialCoachAvailability ?? []);
      }
      clearFormErrors('coachAvailability');
    }, [sameScheduleForMultipleDays, setValue, initialCoachAvailability, clearFormErrors]);

    // Sync coachVenues from the hook to storedAddresses
    useEffect(() => {
      if (coachVenues.length > 0) {
        const newAddresses = coachVenues.filter(
          (newAddress) => !storedAddresses.some((address) => address.id === newAddress.id),
        );

        if (newAddresses.length > 0) {
          setStoredAddresses([...storedAddresses, ...newAddresses]);
        }
      }
    }, [coachVenues]);

    useEffect(() => {
      if (schedule && schedule.length && fields.length === 0) {
        const mergedCoachAvailability = mergeCoachAvailability(schedule);
        setInitialCoachAvailability(JSON.parse(JSON.stringify(mergedCoachAvailability)));
        append(mergedCoachAvailability);
      } else if (schedule?.length === 0) {
        setInitialCoachAvailability(DEFAULT_COACH_AVAILABILITY);
      }
    }, [schedule, fields]);

    const scheduleForm = (
      <form
        className={classNames(
          'flex h-full flex-col',
          inDrawer && 'divide-y divide-gray-200 bg-white shadow-xl',
          wrapperClassName,
        )}
        onSubmit={handleSubmit(async (data) => {
          try {
            setLoading();

            const dateTimeSlots = unMergedDTimeSlots(data?.coachAvailability, storedAddresses);

            await updateCoachAvailability({
              variables: {
                userId: userId,
                objects: dateTimeSlots.reducedData,
              },
            });

            handleSuccessfulEdit?.();
            // clearStoredAddresses(); // REMOVED: Causes race condition - refetch updates venues naturally
            await refetchCoachVenues();
            setOpenDrawer?.(false);
            setSuccess();
          } catch (e) {
            captureException(e);
          }
        })}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          {inDrawer && (
            <div className="flex items-center justify-between border-b border-color-border-input-lightmode px-4 py-6 dark:border-color-border-input-darkmode sm:px-6">
              <Dialog.Title className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Working Schedule
              </Dialog.Title>
              <div className="ml-3 flex h-7 items-center">
                <ButtonText
                  onClick={() => setOpenDrawer?.(false)}
                  className="right-5 top-5 rounded-full p-2 font-medium transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                >
                  <CloseIcon className="h-6 w-6" />
                </ButtonText>
              </div>
            </div>
          )}
          <div className={classNames('mb-5', inDrawer && 'mt-6 px-6')}>
            <Switcher
              name={`sameScheduleForMultipleDays`}
              control={control}
              errors={errors}
              label="Same Schedule for all days"
              invert={invert}
              brand={invert}
            />
          </div>
          {sameScheduleForMultipleDays ? (
            <div className={classNames('py-2', inDrawer && 'px-6')}>
              <div className="mb-6 flex gap-5 ">
                {fields.map((day, index) => {
                  const field = `coachAvailability.${index}` as const;
                  return (
                    <div className="inline-flex flex-col gap-2" key={day.id}>
                      <CheckboxType
                        name={`${field}.checked`}
                        label=""
                        control={control}
                        checkBoxClass="h-4 w-4"
                        textClass=""
                        labelClassName="flex"
                        invert={invert}
                      ></CheckboxType>
                      <p className="typography-product-subheading mr-2 w-7 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                        {dayOfWeekToShortName(day.dayOfWeek as DayOfWeekEnum)}
                      </p>
                    </div>
                  );
                })}
              </div>

              {getScheduleItemForMultipleDays(fields, addTimeSlot, removeTimeSlot)}
            </div>
          ) : (
            <div
              className={classNames(
                'relative flex flex-1 flex-col gap-3  py-2',
                inDrawer && 'overflow-y-auto px-6',
              )}
            >
              {fields.map((day, index) => {
                const field = `coachAvailability.${index}` as const;
                const isChecked = watch(`${field}.checked` as const);
                return (
                  <div key={day.id} className="flex">
                    <div
                      className={classNames(
                        'relative flex w-full min-w-0  gap-2',
                        !isChecked && 'items-center',
                      )}
                    >
                      <div className="flex h-10 items-center pl-1">
                        <CheckboxType
                          name={`${field}.checked`}
                          label=""
                          control={control}
                          checkBoxClass="h-4 w-4"
                          textClass=""
                          labelClassName={classNames('flex')}
                          invert={invert}
                        ></CheckboxType>
                        <p
                          className={classNames(
                            'typography-product-subheading mr-2 w-7 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                            invert && '!text-color-text-darkmode-placeholder',
                          )}
                        >
                          {dayOfWeekToShortName(day.dayOfWeek as DayOfWeekEnum)}
                        </p>
                      </div>
                      {isChecked ? (
                        <div className="min-w-0 flex-1">
                          {day?.timeSlots?.map((availabilityOnDay, indexSlot: number) => {
                            const isEndTimeExceedingLimit =
                              availabilityOnDay.endTimeMinutes ===
                                hourOptions[hourOptions.length - 1].minutesAfterMidnight ||
                              availabilityOnDay.endTimeMinutes > MAX_END_TIME_MINUTES;

                            const key = JSON.stringify(availabilityOnDay); // unique key is necessary to let the form pick new values
                            const venueFieldName = `coachAvailability.${index}.timeSlots.${indexSlot}.venueId`;
                            const startTimeFieldName = `coachAvailability.${index}.timeSlots.${indexSlot}.startTimeMinutes`;
                            const endTimeFieldName = `coachAvailability.${index}.timeSlots.${indexSlot}.endTimeMinutes`;
                            return (
                              <Fragment key={key}>
                                <div className={'mb-3 flex items-center justify-between'}>
                                  <div className="min-w-0 flex-grow">
                                    <ScheduleItem
                                      control={control}
                                      venueFieldName={venueFieldName}
                                      startTimeFieldName={startTimeFieldName}
                                      invert={invert}
                                      endTimeFieldName={endTimeFieldName}
                                      venues={storedAddresses}
                                      onAddVenue={() => {
                                        setOpenDrawer?.(false);
                                        handleTriggerAddVenue(
                                          venueFieldName as CoachAvailabilityPath,
                                        );
                                      }}
                                    />
                                  </div>
                                  <div className="ml-2  flex w-5 shrink-0 items-center space-x-2">
                                    <CloseIcon
                                      onClick={() => {
                                        removeTimeSlot(index, indexSlot);
                                      }}
                                      className={classNames(
                                        indexSlot > 0 && '!block',
                                        'hidden w-5 flex-shrink-0',
                                        invert && 'text-color-text-lightmode-invert',
                                      )}
                                    />
                                  </div>
                                </div>

                                {indexSlot === day?.timeSlots?.length - 1 &&
                                  !isEndTimeExceedingLimit && (
                                    <span
                                      className="typography-product-button-label-large inline-flex cursor-pointer gap-1 text-color-brand-primary max-sm:flex max-sm:justify-end max-sm:pr-7"
                                      onClick={() => {
                                        addTimeSlot(index);
                                      }}
                                    >
                                      <PlusPlain className="h-5 w-5" />
                                      Add Time
                                    </span>
                                  )}
                              </Fragment>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="typography-product-body-highlight text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                          Not Selected
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {inDrawer && (
          <div className="flex justify-end gap-3 border-t border-color-border-input-lightmode pb-4 dark:border-color-border-input-darkmode md:px-5  md:pt-4">
            <Button
              variant="primary"
              type="submit"
              size="lg"
              className="max-w-32"
              disabled={isLoading}
            >
              Save
            </Button>
          </div>
        )}
      </form>
    );

    const addVenuePopup = (
      <AddVenue
        title="Add a venue"
        isOpen={isVenueModalOpen}
        onClose={handleCloseVenueModal}
        onSubmit={(venue) => {
          handleAddVenue({
            address: venue?.addressString || '',
            id: venue?.id,
            locationType: LocationType.Venue,
          } as VenueAddress);
          handleCloseVenueModal();
        }}
        enableManualAddress={true}
        onSubmitManualAddress={(val) => {
          handleAddVenue({
            address: val.address || '',
            id: '',
            latitude: val?.latitude,
            longitude: val?.longitude,
            locationType: LocationType.Custom,
          } as VenueAddress);
          handleCloseVenueModal();
        }}
      />
    );

    if (inDrawer) {
      return (
        <>
          <Transition.Root show={openDrawer} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-100 "
              onClose={() => setOpenDrawer?.(false) || null}
            >
              <div className="fixed inset-0" />
              <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full max-w-[40rem] pl-10">
                    <Transition.Child
                      as={Fragment}
                      enter="transform transition ease-in-out duration-500 sm:duration-700"
                      enterFrom="translate-x-full"
                      enterTo="translate-x-0"
                      leave="transform transition ease-in-out duration-500 sm:duration-700"
                      leaveFrom="translate-x-0"
                      leaveTo="translate-x-full"
                    >
                      <Dialog.Panel className="pointer-events-auto w-full">
                        {scheduleForm}
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </div>
            </Dialog>
          </Transition.Root>
          {addVenuePopup}
        </>
      );
    }

    return (
      <>
        {scheduleForm}
        {addVenuePopup}
      </>
    );
  },
);

export default EditSchedule;
